// Engine audio: AudioContext lifecycle and loop-bank loading. Browser code, no DOM screens here.
// Design: docs/superpowers/specs/2026-10-14-engine-range-game-design.md §11
import type { BankManifest, LoopDef } from '../types';

/** Shown to the player whenever the engine bank cannot be made audible. */
export const AUDIO_UNAVAILABLE = 'Engine audio unavailable';

let ctx: AudioContext | null = null;
let keepAlive: HTMLAudioElement | null = null;

/**
 * One second of 8 kHz 8-bit mono silence as a data: URI. A looping <audio> element playing this from the
 * first gesture upgrades the page's audio session on iOS so Web Audio keeps sounding with the mute switch
 * on (WebKit treats bare Web Audio as "ambient"). The element is left unmuted on purpose: WebKit does not
 * count a muted element as playing audio, and the data is digital silence anyway.
 */
function silentWavUri(): string {
  const rate = 8000;
  const samples = rate;
  const header = new ArrayBuffer(44);
  const v = new DataView(header);
  const ascii = (o: number, s: string): void => {
    for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i));
  };
  ascii(0, 'RIFF');
  v.setUint32(4, 36 + samples, true);
  ascii(8, 'WAVE');
  ascii(12, 'fmt ');
  v.setUint32(16, 16, true);
  v.setUint16(20, 1, true); // PCM
  v.setUint16(22, 1, true); // mono
  v.setUint32(24, rate, true);
  v.setUint32(28, rate, true); // byte rate (8-bit mono)
  v.setUint16(32, 1, true); // block align
  v.setUint16(34, 8, true); // bits per sample
  ascii(36, 'data');
  v.setUint32(40, samples, true);
  const bytes = new Uint8Array(44 + samples);
  bytes.set(new Uint8Array(header), 0);
  bytes.fill(128, 44); // 8-bit PCM silence is the mid-point value
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return `data:audio/wav;base64,${btoa(bin)}`;
}

/**
 * Create (or resume) the single AudioContext. Call this from inside a click / touch handler: browsers
 * only let a context start inside a user gesture. Safe to call repeatedly. Never throws.
 */
export function unlockAudio(): void {
  try {
    if (!ctx) {
      const Ctor = window.AudioContext;
      if (typeof Ctor !== 'function') return;
      ctx = new Ctor();
    }
    if (ctx.state === 'suspended') void ctx.resume().catch(() => undefined);
    if (!keepAlive && typeof Audio === 'function') {
      const a = new Audio(silentWavUri());
      a.loop = true;
      a.volume = 1;
      a.setAttribute('playsinline', '');
      a.setAttribute('aria-hidden', 'true');
      const p = a.play();
      if (p && typeof p.catch === 'function') p.catch(() => undefined);
      keepAlive = a;
    }
  } catch {
    // Fail soft: a page without working audio still plays the game.
  }
}

export function getAudioContext(): AudioContext | null {
  return ctx;
}

/** Opus-in-WebM when the browser decodes it, otherwise the AAC fallback (Safari before 17.4). */
export function preferredExtension(): '.webm' | '.m4a' {
  try {
    if (typeof Audio === 'function' && new Audio().canPlayType('audio/webm; codecs=opus') !== '') return '.webm';
  } catch {
    // fall through to the safe choice
  }
  return '.m4a';
}

function withExtension(file: string, ext: '.webm' | '.m4a'): string {
  return file.replace(/\.(webm|m4a)$/i, '') + ext;
}

function isLoopDef(x: unknown): x is LoopDef {
  if (typeof x !== 'object' || x === null) return false;
  const l = x as Record<string, unknown>;
  return typeof l.file === 'string' && typeof l.rpm === 'number' && (l.load === 'on' || l.load === 'off');
}

export function isBankManifest(x: unknown): x is BankManifest {
  if (typeof x !== 'object' || x === null) return false;
  const m = x as Record<string, unknown>;
  return (
    typeof m.bank === 'string' &&
    typeof m.engine === 'string' &&
    Array.isArray(m.loops) &&
    m.loops.length > 0 &&
    m.loops.every(isLoopDef) &&
    (m.start === undefined || typeof m.start === 'string') &&
    (m.stop === undefined || typeof m.stop === 'string') &&
    Array.isArray(m.credits)
  );
}

export interface LoadedBank {
  manifest: BankManifest;
  /** Keyed by the manifest's file name (extension as written in the manifest, i.e. the Opus name). */
  buffers: Map<string, AudioBuffer>;
}

async function fetchAndDecode(context: AudioContext, url: string): Promise<AudioBuffer> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`${AUDIO_UNAVAILABLE}: ${url} (${res.status})`);
  const bytes = await res.arrayBuffer();
  return context.decodeAudioData(bytes);
}

/**
 * Fetch `/audio/engines/<bank>/manifest.json` and decode every loop and one-shot into AudioBuffers.
 * Progress runs 0..1 across the manifest plus each file. Rejects with an Error whose message starts
 * with AUDIO_UNAVAILABLE when there is no context, the manifest is malformed or a decode fails.
 */
export async function loadBank(bank: string, onProgress?: (p: number) => void): Promise<LoadedBank> {
  const context = ctx;
  if (!context) throw new Error(`${AUDIO_UNAVAILABLE}: audio not unlocked`);
  if (!/^[a-z0-9-]+$/.test(bank)) throw new Error(`${AUDIO_UNAVAILABLE}: bad bank id`);
  const base = `/audio/engines/${bank}/`;
  let manifest: BankManifest;
  try {
    const res = await fetch(`${base}manifest.json`);
    if (!res.ok) throw new Error(String(res.status));
    const json: unknown = await res.json();
    if (!isBankManifest(json)) throw new Error('malformed manifest');
    manifest = json;
  } catch (e) {
    throw new Error(`${AUDIO_UNAVAILABLE}: ${e instanceof Error ? e.message : 'manifest'}`);
  }
  const files: string[] = manifest.loops.map((l) => l.file);
  if (manifest.start) files.push(manifest.start);
  if (manifest.stop) files.push(manifest.stop);
  const total = files.length + 1;
  let done = 1;
  onProgress?.(done / total);
  const ext = preferredExtension();
  const buffers = new Map<string, AudioBuffer>();
  try {
    await Promise.all(
      files.map(async (file) => {
        const buf = await fetchAndDecode(context, base + withExtension(file, ext));
        buffers.set(file, buf);
        done++;
        onProgress?.(done / total);
      }),
    );
  } catch (e) {
    throw new Error(`${AUDIO_UNAVAILABLE}: ${e instanceof Error ? e.message : 'decode failed'}`);
  }
  return { manifest, buffers };
}
